import {
	designProcessingStatus,
	pointCloudProcessingStatus,
} from "@/constant.js";
import type Design from "@/models/Design";
import type { Drawing2dImage } from "@/models/Drawing2DImage";
import type Line from "@/models/Line";
import type LineWorks from "@/models/LineWorks";
import type PointCloud from "@/models/PointCloud";
import { deriveIsAssetUploaded } from "@/models/drawing2dProcessingStatus";
import store from "@/store";
import { type Ref, ref } from "vue";
import type { TrackedDataType, TrackedItem } from "./uploadTracker.types";

/** Partial store state shape used by this composable */
interface UploadTrackerStoreState {
	point_cloud_list: PointCloud[];
	design_list: (Design | Line)[];
	simaLine: { simaLineList: LineWorks[] };
	drawing2dImage: { drawing2dImageList: Drawing2dImage[] };
}

const POLL_INTERVAL_MS = 10_000;

// --- Module-scoped singleton state ---
const trackedItems = new Set<string>();
const trackedCount = ref(0);
let intervalId: ReturnType<typeof setInterval> | null = null;
let currentSiteId: string | null = null;

function makeKey(item: TrackedItem): string {
	return JSON.stringify(item);
}

function parseKey(key: string): TrackedItem {
	return JSON.parse(key) as TrackedItem;
}

function getTrackedDataTypes(): Set<TrackedDataType> {
	const types = new Set<TrackedDataType>();
	for (const key of trackedItems) {
		types.add(parseKey(key).dataType);
	}
	return types;
}

function getAppState(): UploadTrackerStoreState {
	return store.state as unknown as UploadTrackerStoreState;
}

function checkItem(item: Readonly<TrackedItem>): boolean {
	const appState = getAppState();

	if (item.dataType === "pointcloud") {
		const model = appState.point_cloud_list.find(
			(pc) => pc.getId() === item.id,
		);
		if (!model) return false;
		const status = model.getProcessingStatus();
		if (pointCloudProcessingStatus.FINISHED.includes(status)) {
			model.setIsAssetUploaded("DONE");
			return true;
		}
		if (pointCloudProcessingStatus.ERROR.includes(status)) {
			model.setIsAssetUploaded("ERROR");
			return true;
		}
		return false;
	}

	if (item.dataType === "design") {
		const model = appState.design_list.find(
			(d) => d.getId() === item.id && d.getType() === "design",
		);
		if (!model) return false;
		const status = model.getProcessingStatus();
		if (status === "SST_FINISHED") {
			model.setIsAssetUploaded("DONE");
			return true;
		}
		if (designProcessingStatus.ERROR.includes(status)) {
			model.setIsAssetUploaded("ERROR");
			return true;
		}
		return false;
	}

	if (item.dataType === "lineworks") {
		const model = appState.design_list.find(
			(d) => d.getId() === item.id && d.getType() === "lineworks",
		);
		if (!model) return false;
		const status = model.getProcessingStatus();
		if (status === "complete_wgs84") {
			model.setIsAssetUploaded("DONE");
			return true;
		}
		if (designProcessingStatus.ERROR.includes(status)) {
			model.setIsAssetUploaded("ERROR");
			return true;
		}
		return false;
	}

	if (item.dataType === "drawing2dImage") {
		const model = appState.drawing2dImage.drawing2dImageList.find(
			(image) => image.getId() === item.id,
		);
		if (!model) return false;
		// DONE / ERROR の判定は drawing2dProcessingStatus に集約する（PointCloud と同じ 3 値）
		const derived = deriveIsAssetUploaded(model.getProcessingStatus());
		if (derived === "PENDING") return false;
		model.setIsAssetUploaded(derived);
		return true;
	}

	if (item.dataType === "sima") {
		const model = appState.simaLine.simaLineList.find(
			(l) => l.lineId === item.id,
		);
		if (!model) return false;
		const status = model.processingStatus;
		if (
			designProcessingStatus.FINISHED.includes(status) ||
			designProcessingStatus.ERROR.includes(status)
		) {
			return true;
		}
		return false;
	}

	return false;
}

async function pollOnce(): Promise<void> {
	if (trackedItems.size === 0) {
		stopPollingInternal();
		return;
	}

	const siteId = currentSiteId;
	if (!siteId) return;

	const types = getTrackedDataTypes();
	const dispatches: Promise<unknown>[] = [];

	if (types.has("pointcloud")) {
		dispatches.push(store.dispatch("get_point_cloud_list", siteId));
	}
	if (types.has("design") || types.has("lineworks")) {
		dispatches.push(store.dispatch("get_design_list", siteId));
	}
	if (types.has("sima")) {
		dispatches.push(store.dispatch("simaLine/getSimaLineList", siteId));
	}
	if (types.has("drawing2dImage")) {
		dispatches.push(
			store.dispatch("drawing2dImage/getDrawing2dImageList", siteId),
		);
	}

	await Promise.allSettled(dispatches);

	const toRemove: string[] = [];
	for (const key of trackedItems) {
		if (checkItem(parseKey(key))) {
			toRemove.push(key);
		}
	}
	for (const key of toRemove) {
		trackedItems.delete(key);
	}
	trackedCount.value = trackedItems.size;

	if (trackedItems.size === 0) {
		stopPollingInternal();
	}
}

function startPollingInternal(): void {
	if (intervalId !== null) return;
	intervalId = setInterval(() => {
		void pollOnce();
	}, POLL_INTERVAL_MS);
}

function stopPollingInternal(): void {
	if (intervalId !== null) {
		clearInterval(intervalId);
		intervalId = null;
	}
}

function trackItem(item: Readonly<TrackedItem>, siteId: string): void {
	currentSiteId = siteId;
	const key = makeKey(item);
	if (trackedItems.has(key)) return;
	trackedItems.add(key);
	trackedCount.value = trackedItems.size;
	startPollingInternal();
}

function detectAndTrackPendingItems(
	pointCloudList: ReadonlyArray<PointCloud>,
	designList: ReadonlyArray<Design | Line>,
	simaLineList: ReadonlyArray<LineWorks>,
	siteId: string,
	drawing2dImageList: ReadonlyArray<Drawing2dImage> = [],
): void {
	currentSiteId = siteId;
	for (const pc of pointCloudList) {
		const status = pc.getProcessingStatus();
		if (pointCloudProcessingStatus.PROCESSING.includes(status)) {
			trackItem({ dataType: "pointcloud", id: pc.getId() }, siteId);
		} else if (pointCloudProcessingStatus.FINISHED.includes(status)) {
			pc.setIsAssetUploaded("DONE");
		}
	}

	for (const item of designList) {
		const status = item.getProcessingStatus();
		if (item.getType() === "design") {
			if (designProcessingStatus.PROCESSING.includes(status)) {
				trackItem({ dataType: "design", id: item.getId() }, siteId);
			} else if (
				status === "SST_FINISHED" ||
				status === "complete" ||
				status === "complete_wgs84"
			) {
				item.setIsAssetUploaded("DONE");
			}
		} else if (item.getType() === "lineworks") {
			if (designProcessingStatus.PROCESSING.includes(status)) {
				trackItem({ dataType: "lineworks", id: item.getId() }, siteId);
			} else if (status === "complete_wgs84") {
				item.setIsAssetUploaded("DONE");
			}
		}
	}

	for (const line of simaLineList) {
		const status = line.processingStatus;
		if (designProcessingStatus.PROCESSING.includes(status)) {
			trackItem({ dataType: "sima", id: line.lineId }, siteId);
		}
	}

	// isAssetUploaded は一覧取得時に Drawing2dImage.update(row) が導出済みなので、ここでは処理中の行を拾うだけ
	for (const image of drawing2dImageList) {
		if (deriveIsAssetUploaded(image.getProcessingStatus()) === "PENDING") {
			trackItem({ dataType: "drawing2dImage", id: image.getId() }, siteId);
		}
	}
}

function stopPolling(): void {
	stopPollingInternal();
}

export function useUploadTracker(): {
	trackItem: (item: Readonly<TrackedItem>, siteId: string) => void;
	trackedCount: Readonly<Ref<number>>;
	detectAndTrackPendingItems: (
		pointCloudList: ReadonlyArray<PointCloud>,
		designList: ReadonlyArray<Design | Line>,
		simaLineList: ReadonlyArray<LineWorks>,
		siteId: string,
		drawing2dImageList?: ReadonlyArray<Drawing2dImage>,
	) => void;
	stopPolling: () => void;
} {
	return {
		trackItem,
		trackedCount: trackedCount as Readonly<Ref<number>>,
		detectAndTrackPendingItems,
		stopPolling,
	};
}
